import { CONFIG } from '../config';
import { supabase } from './supabase';

// Analytics event types
export const AnalyticsEvents = {
  WALLET: {
    CONNECT: 'wallet_connect',
    DISCONNECT: 'wallet_disconnect',
    SWITCH_CHAIN: 'wallet_switch_chain',
    ERROR: 'wallet_error'
  },
  NFT: {
    STAKE: 'nft_stake',
    UNSTAKE: 'nft_unstake',
    VIEW: 'nft_view',
    SEARCH: 'nft_search'
  },
  CONTRACT: {
    INTERACTION: 'contract_interaction',
    ERROR: 'contract_error'
  },
  USER: {
    PAGE_VIEW: 'page_view',
    FEATURE_USE: 'feature_use'
  }
} as const;

// Analytics data types
interface WalletEventData {
  address?: string;
  chainId?: number;
  provider?: string;
  timestamp: number;
  success: boolean;
  error?: string;
}

interface NFTEventData {
  collectionAddress: string;
  tokenId?: string;
  action: string;
  timestamp: number;
  userAddress?: string;
}

interface ContractEventData {
  address: string;
  method: string;
  timestamp: number;
  status: 'success' | 'error';
  error?: string;
  gasUsed?: string;
}

interface UserEventData {
  page?: string;
  feature?: string;
  timestamp: number;
  sessionId: string;
  duration?: number;
}

interface SessionUpdateData {
  user_address?: string;
  end_time?: string;
}

// Analytics service
export class Analytics {
  private static instance: Analytics;
  private sessionId: string;
  private initialized: boolean = false;
  private sessionDbId?: string;
  private initializationPromise: Promise<void>;

  private constructor() {
    this.sessionId = this.generateSessionId();
    this.initializationPromise = this.initializeAnalytics();
  }

  public static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  }

  private async initializeAnalytics() {
    if (typeof window !== 'undefined' && !this.initialized) {
      try {
        // Initialize Vercel Analytics
        import('@vercel/analytics').then(({ inject }) => {
          inject({
            debug: CONFIG.VERCEL.ANALYTICS_DEBUG,
            defaultData: {
              app: 'FrostScanner',
              version: '1.0.0'
            }
          });
        });

        // Create analytics session
        const { data: session, error } = await supabase
          .from('analytics_sessions')
          .insert({
            session_id: this.sessionId,
            user_agent: navigator.userAgent,
            referrer: document.referrer
          })
          .select('id')
          .single();

        if (error) {
          console.error('Failed to create analytics session:', error);
          throw error;
        }

        this.sessionDbId = session.id;
        this.initialized = true;
      } catch (error) {
        console.error('Analytics initialization failed:', error);
        throw error;
      }
    }
  }

  public async updateSession(data: SessionUpdateData): Promise<void> {
    try {
      await this.initializationPromise;
      
      if (!this.sessionDbId) {
        throw new Error('Session not initialized');
      }

      const { error } = await supabase
        .from('analytics_sessions')
        .update(data)
        .eq('id', this.sessionDbId);

      if (error) {
        console.error('Failed to update session:', error);
        throw error;
      }
    } catch (error) {
      console.error('Session update failed:', error);
      throw error;
    }
  }

  // Wallet Events
  public async trackWalletEvent(
    eventName: keyof typeof AnalyticsEvents.WALLET,
    data: WalletEventData
  ) {
    try {
      await this.initializationPromise;

      if (!this.sessionDbId) {
        throw new Error('Session not initialized');
      }

      await this.trackEvent(AnalyticsEvents.WALLET[eventName], {
        ...data,
        chainName: CONFIG.CHAIN_NAME,
        sessionId: this.sessionId
      });

      const { error } = await supabase.from('wallet_events').insert({
        session_id: this.sessionDbId,
        event_type: eventName,
        wallet_address: data.address, // Updated to match new schema
        chain_id: data.chainId,
        provider: data.provider,
        timestamp: new Date(data.timestamp),
        success: data.success,
        error: data.error,
        metadata: {
          chainName: CONFIG.CHAIN_NAME
        }
      });

      if (error) {
        console.error('Failed to track wallet event:', error);
        throw error;
      }
    } catch (error) {
      console.error('Wallet event tracking failed:', error);
      throw error;
    }
  }

  // NFT Events
  public async trackNFTEvent(
    eventName: keyof typeof AnalyticsEvents.NFT,
    data: NFTEventData
  ) {
    try {
      await this.initializationPromise;

      if (!this.sessionDbId) {
        throw new Error('Session not initialized');
      }

      await this.trackEvent(AnalyticsEvents.NFT[eventName], {
        ...data,
        chainId: CONFIG.CHAIN_ID,
        sessionId: this.sessionId
      });

      const { error } = await supabase.from('nft_events').insert({
        session_id: this.sessionDbId,
        collection_address: data.collectionAddress,
        token_id: data.tokenId,
        action: data.action,
        user_address: data.userAddress,
        timestamp: new Date(data.timestamp),
        metadata: {
          chainId: CONFIG.CHAIN_ID
        }
      });

      if (error) {
        console.error('Failed to track NFT event:', error);
        throw error;
      }
    } catch (error) {
      console.error('NFT event tracking failed:', error);
      throw error;
    }
  }

  // Contract Events
  public async trackContractEvent(
    eventName: keyof typeof AnalyticsEvents.CONTRACT,
    data: ContractEventData
  ) {
    try {
      await this.initializationPromise;

      if (!this.sessionDbId) {
        throw new Error('Session not initialized');
      }

      await this.trackEvent(AnalyticsEvents.CONTRACT[eventName], {
        ...data,
        chainId: CONFIG.CHAIN_ID,
        sessionId: this.sessionId
      });

      const { error } = await supabase.from('contract_events').insert({
        session_id: this.sessionDbId,
        contract_address: data.address,
        method: data.method,
        status: data.status,
        gas_used: data.gasUsed,
        error: data.error,
        timestamp: new Date(data.timestamp),
        metadata: {
          chainId: CONFIG.CHAIN_ID
        }
      });

      if (error) {
        console.error('Failed to track contract event:', error);
        throw error;
      }
    } catch (error) {
      console.error('Contract event tracking failed:', error);
      throw error;
    }
  }

  // User Events
  public async trackUserEvent(
    eventName: keyof typeof AnalyticsEvents.USER,
    data: UserEventData
  ) {
    try {
      await this.initializationPromise;

      if (!this.sessionDbId) {
        throw new Error('Session not initialized');
      }

      await this.trackEvent(AnalyticsEvents.USER[eventName], {
        ...data,
        sessionId: this.sessionId
      });

      const { error } = await supabase.from('user_events').insert({
        session_id: this.sessionDbId,
        event_type: eventName,
        page: data.page,
        feature: data.feature,
        duration: data.duration,
        timestamp: new Date(data.timestamp),
        metadata: {
          url: window.location.href
        }
      });

      if (error) {
        console.error('Failed to track user event:', error);
        throw error;
      }
    } catch (error) {
      console.error('User event tracking failed:', error);
      throw error;
    }
  }

  // Core tracking method
  private async trackEvent(eventName: string, data: any) {
    if (typeof window !== 'undefined') {
      try {
        await this.initializationPromise;

        const analyticsEvent = {
          name: eventName,
          properties: {
            ...data,
            environment: process.env.NODE_ENV,
            timestamp: Date.now(),
            url: window.location.href,
            referrer: document.referrer,
            userAgent: navigator.userAgent,
          }
        };

        // Send to Vercel Analytics
        if (window.va?.track) {
          window.va.track(analyticsEvent.name, analyticsEvent.properties);
        }

        // Log in development
        if (CONFIG.VERCEL.ANALYTICS_DEBUG) {
          console.log('Analytics Event:', analyticsEvent);
        }
      } catch (error) {
        console.error('Event tracking failed:', error);
        throw error;
      }
    }
  }
}

// Analytics hook
export function useAnalytics() {
  return Analytics.getInstance();
}